import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import WidgetBox from "../../../components/widgetBox/WidgetBox";

import {
  SppCustomGroupEnum,
  SppGroupEnum,
  SppMacroGroupEnum,
  TargetTypeEnum,
} from "../../../classes/enums/SppEnum";
import OctTab from "./OctTab";
import SppParameterGroup from "./SppGroup";
import "./SppWidget.css";

import { useCustomMeasurements } from "../../../classes/contexts/CustomMeasurementsContext";
import useKeyParameterSync from "../../../hooks/useKeyParameterSync";

const SppWidget = ({
  updateParamsWithValues,
  onSppSelected,
  onSppSelectedCustom,
  removeCustomParameter,
  activeParameters,
  measurementConfigs = {},
  customMeasuramentConfigs = {},
  isOctView,
  selectedTargetType = TargetTypeEnum.GAP,
  showCoronalParameters = true,
  digitalTwinId = null,
  initialKeyParameters = [],
  onFavoritesChange = null,
  isSettingsMode = false,
  compareMode = false,
  currentPlanLabel = "Plan",
  comparedPlanLabel = "Plan",
  isDisabled = false,
}) => {
  // DEBUG: Log props received
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [
    previousCustomMeasurementsLength,
    setPreviousCustomMeasurementsLength,
  ] = useState(0);
  const [favoriteParams, setFavoriteParams] = useState(new Set());
  const [hasInitializedKeyParams, setHasInitializedKeyParams] = useState(false);

  const { customMeasurements } = useCustomMeasurements();
  const widgetRef = useRef(null);
  const { syncKeyParametersWithDigitalTwin, cleanup } =
    useKeyParameterSync(digitalTwinId);

  const handleToggleFavorite = useCallback(
    paramKey => {
      const normalizedKey = Number(paramKey);
      const keyToUse = Number.isFinite(normalizedKey)
        ? normalizedKey
        : paramKey;
      setFavoriteParams(prev => {
        const newFavorites = new Set(prev);
        const hasNormalized = newFavorites.has(keyToUse);
        const hasRaw =
          !Number.isFinite(normalizedKey) && newFavorites.has(paramKey);

        if (hasNormalized || hasRaw) {
          if (hasNormalized) {
            newFavorites.delete(keyToUse);
          }
          if (hasRaw) {
            newFavorites.delete(paramKey);
          }
        } else {
          newFavorites.add(keyToUse);
        }

        if (digitalTwinId && hasInitializedKeyParams) {
          syncKeyParametersWithDigitalTwin(newFavorites);
        }

        if (onFavoritesChange && hasInitializedKeyParams) {
          const favoritesArray = Array.from(newFavorites)
            .map(key => Number(key))
            .filter(Number.isFinite);
          onFavoritesChange(favoritesArray);
        }

        return newFavorites;
      });
    },
    [
      digitalTwinId,
      hasInitializedKeyParams,
      syncKeyParametersWithDigitalTwin,
      onFavoritesChange,
    ]
  );

  const enrichedCustomConfigs = useMemo(() => {
    const enrichConfig = config => {
      return config.map(param => {
        const measurementKey = param.key || param.unique_identifier;
        const liveValue = customMeasurements[measurementKey]?.value;

        return {
          ...param,

          originalValue:
            liveValue !== undefined ? liveValue : param.originalValue,
          value: liveValue !== undefined ? liveValue : param.value,

          isLiveValue: liveValue !== undefined,
        };
      });
    };

    return {
      advancedMeasurements: customMeasuramentConfigs?.advancedMeasurements
        ? enrichConfig(customMeasuramentConfigs.advancedMeasurements)
        : [],
      distance: customMeasuramentConfigs?.distance
        ? enrichConfig(customMeasuramentConfigs.distance)
        : [],
      angle: customMeasuramentConfigs?.angle
        ? enrichConfig(customMeasuramentConfigs.angle)
        : [],
    };
  }, [customMeasuramentConfigs, customMeasurements]);

  const customParameterGroups = useMemo(() => {
    const groups = [];

    if (enrichedCustomConfigs.advancedMeasurements?.length > 0) {
      groups.push({
        title: "Advanced Measurements",
        config: enrichedCustomConfigs.advancedMeasurements,
        category: SppCustomGroupEnum.ADVANCED_MEASUREMENTS,
      });
    }

    if (enrichedCustomConfigs.distance?.length > 0) {
      groups.push({
        title: "Distance",
        config: enrichedCustomConfigs.distance,
        category: SppCustomGroupEnum.DISTANCE,
      });
    }

    if (enrichedCustomConfigs.angle?.length > 0) {
      groups.push({
        title: "Angle",
        config: enrichedCustomConfigs.angle,
        category: SppCustomGroupEnum.ANGLE,
      });
    }

    return groups;
  }, [enrichedCustomConfigs]);

  const allParameterGroups = useMemo(() => {
    const macroGroups = [];
    const generalGroups = [];
    const gapGroups = [];
    const saasGroups = [];
    const schwabGroups = [];

    if (measurementConfigs?.general?.length > 0) {
      generalGroups.push({
        title: SppGroupEnum.OTHER,
        config: measurementConfigs.general,
        isSimple: true,
        category: SppGroupEnum.OTHER,
        allowFavorites: true,
      });
    }

    if (measurementConfigs?.pelvisParameters?.length > 0) {
      generalGroups.push({
        title: SppGroupEnum.PELVIS,
        category: SppGroupEnum.PELVIS,
        config: measurementConfigs.pelvisParameters,
        allowFavorites: true,
      });
    }

    if (measurementConfigs?.lumbarLordosisParameters?.length > 0) {
      generalGroups.push({
        title: SppGroupEnum.LUMBAR_LORDOSIS,
        category: SppGroupEnum.LUMBAR_LORDOSIS,
        config: measurementConfigs.lumbarLordosisParameters,
        allowFavorites: true,
      });
    }

    if (measurementConfigs?.thoracicKyphosisParameters?.length > 0) {
      generalGroups.push({
        title: SppGroupEnum.THORACIC_KYPHOSIS,
        category: SppGroupEnum.THORACIC_KYPHOSIS,
        config: measurementConfigs.thoracicKyphosisParameters,
        allowFavorites: true,
      });
    }

    if (measurementConfigs?.sagittalAlignmentParameters?.length > 0) {
      generalGroups.push({
        title: SppGroupEnum.SAGITTAL,
        category: SppGroupEnum.SAGITTAL,
        config: measurementConfigs.sagittalAlignmentParameters,
        allowFavorites: true,
      });
    }

    if (measurementConfigs?.cervicalParameters?.length > 0) {
      generalGroups.push({
        title: SppGroupEnum.CERVICAL,
        category: SppGroupEnum.CERVICAL,
        config: measurementConfigs.cervicalParameters,
        allowFavorites: true,
      });
    }

    if (
      measurementConfigs?.coronalParameters?.length > 0 &&
      showCoronalParameters
    ) {
      generalGroups.push({
        title: SppMacroGroupEnum.CORONAL,
        category: SppMacroGroupEnum.CORONAL,
        config: measurementConfigs.coronalParameters,
        allowFavorites: true,
      });
    }

    if (generalGroups.length > 0) {
      macroGroups.push({
        sectionTitle: SppMacroGroupEnum.GENERAL,
        groups: generalGroups,
      });
    }

    if (measurementConfigs?.globalAlignmentParameters?.length > 0) {
      gapGroups.push({
        title: SppMacroGroupEnum.GLOBAL,
        category: SppMacroGroupEnum.GLOBAL,
        config: measurementConfigs.globalAlignmentParameters,
        allowFavorites: false,
      });
    }

    if (gapGroups.length > 0) {
      macroGroups.push({
        sectionTitle: SppMacroGroupEnum.GLOBAL,
        groups: gapGroups,
      });
    }

    if (measurementConfigs?.saasParameters?.length > 0) {
      saasGroups.push({
        title: SppMacroGroupEnum.SAAS,
        category: SppMacroGroupEnum.SAAS,
        config: measurementConfigs.saasParameters,
        allowFavorites: false,
      });
    }

    if (saasGroups.length > 0) {
      macroGroups.push({
        sectionTitle: SppMacroGroupEnum.SAAS,
        groups: saasGroups,
      });
    }

    if (measurementConfigs?.sagittalSchwabModifiers?.length > 0) {
      schwabGroups.push({
        title: SppMacroGroupEnum.SAGITTAL_SCHWAB,
        category: SppMacroGroupEnum.SAGITTAL_SCHWAB,
        config: measurementConfigs.sagittalSchwabModifiers,
        allowFavorites: false,
      });
    }

    if (schwabGroups.length > 0) {
      macroGroups.push({
        sectionTitle: SppMacroGroupEnum.SAGITTAL_SCHWAB,
        groups: schwabGroups,
      });
    }

    return macroGroups;
  }, [measurementConfigs, showCoronalParameters]);

  const { keyParameterGroups, filteredParameterGroups } = useMemo(() => {
    if (favoriteParams.size === 0) {
      return {
        keyParameterGroups: [],
        filteredParameterGroups: allParameterGroups,
      };
    }

    const keyParams = [];

    const filteredGroups = allParameterGroups
      .map((section, sectionIndex) => {
        if (sectionIndex === 0) {
          const filteredSectionGroups = section.groups
            .map(group => {
              const favoriteParameters = group.config.filter(param => {
                const isFavorite = favoriteParams.has(param.key);
                return isFavorite;
              });

              const nonFavoriteParameters = group.config.filter(
                param => !favoriteParams.has(param.key)
              );

              keyParams.push(...favoriteParameters);

              return {
                ...group,
                config: nonFavoriteParameters,
              };
            })
            .filter(group => group.config.length > 0);

          return {
            ...section,
            groups: filteredSectionGroups,
          };
        }
        return section;
      })
      .filter(section => section.groups.length > 0);

    const keyParameterGroups =
      keyParams.length > 0
        ? [
            {
              title: "Key Parameters",
              config: keyParams,
              category: "KEY_PARAMETERS",
              isSimple: false,
              allowFavorites: true,
            },
          ]
        : [];

    return { keyParameterGroups, filteredParameterGroups: filteredGroups };
  }, [allParameterGroups, favoriteParams]);

  const [hasInitialized, setHasInitialized] = useState(false);

  const totalGroups = useMemo(() => {
    let count = filteredParameterGroups.length;
    if (customParameterGroups.length > 0) {
      count += 1;
    }
    if (keyParameterGroups.length > 0) {
      count += 1;
    }
    return count;
  }, [
    filteredParameterGroups.length,
    customParameterGroups.length,
    keyParameterGroups.length,
  ]);

  useEffect(() => {
    if (widgetRef.current) {
      widgetRef.current.style.setProperty("--total-groups", totalGroups);
    }
  }, [totalGroups]);

  useEffect(() => {
    if (!hasInitialized && hasInitializedKeyParams) {
      setExpandedGroup(null);
      setHasInitialized(true);
    }
  }, [
    keyParameterGroups,
    filteredParameterGroups,
    hasInitialized,
    hasInitializedKeyParams,
    favoriteParams.size,
  ]);

  useEffect(() => {
    if (!widgetRef.current || !expandedGroup) return;

    const calculateExpandedSectionHeight = () => {
      const container = widgetRef.current;
      const containerHeight = container.clientHeight;

      const containerStyles = getComputedStyle(container);
      const containerPadding =
        parseInt(containerStyles.paddingTop) +
        parseInt(containerStyles.paddingBottom);
      const gapsBetweenSections = parseInt(containerStyles.gap) || 10;
      const headerHeight =
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--MDsim-widget-box-title-height-small"
          )
        ) || 40;

      const totalSections =
        filteredParameterGroups.length +
        (customParameterGroups.length > 0 ? 1 : 0);
      const collapsedSectionsCount = totalSections - 1;

      const keyParametersSection = container.querySelector(
        ".App-widget-box:first-child"
      );
      const keyParametersHeight = keyParametersSection
        ? keyParametersSection.offsetHeight
        : 0;

      const usedSpace =
        containerPadding +
        keyParametersHeight +
        collapsedSectionsCount * headerHeight +
        totalSections * gapsBetweenSections;

      const availableHeight = containerHeight - usedSpace - headerHeight;

      container.style.setProperty(
        "--expanded-content-max-height",
        `${Math.max(200, availableHeight)}px`
      );
    };

    requestAnimationFrame(() => {
      calculateExpandedSectionHeight();
    });

    const handleResize = () => {
      requestAnimationFrame(() => {
        calculateExpandedSectionHeight();
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [
    expandedGroup,
    filteredParameterGroups.length,
    customParameterGroups.length,
    keyParameterGroups.length,
  ]);

  const renderParameterGroups = useMemo(
    () =>
      (sppGroups, isCustom = false, hideHeader = false) => {
        return sppGroups.map(group => {
          const updatedConfig = isCustom
            ? group.config
            : updateParamsWithValues(group.config);

          // DEBUG: Log what's being rendered for each group
          if (!isCustom && updatedConfig.length > 0) {
            const sampleWithValues = updatedConfig.find(p => p.currentValue !== undefined || p.comparedCurrentValue !== undefined);
          }

          return (
            <SppParameterGroup
              key={group.title}
              title={group.title}
              category={group.category}
              parameters={updatedConfig}
              onSppSelected={onSppSelected}
              onSppSelectedCustom={onSppSelectedCustom}
              removeCustomParameter={removeCustomParameter}
              activeParameters={activeParameters}
              isCustom={isCustom}
              isSimple={group.isSimple}
              isOctView={isOctView}
              hideHeader={hideHeader}
              onToggleFavorite={handleToggleFavorite}
              favoriteParams={favoriteParams}
              selectedTargetType={selectedTargetType}
              allowFavorites={group.allowFavorites}
              isSettingsMode={isSettingsMode}
              compareMode={compareMode}
              currentPlanLabel={currentPlanLabel}
              comparedPlanLabel={comparedPlanLabel}
              disabled={isDisabled}
            />
          );
        });
      },
    [
      updateParamsWithValues,
      onSppSelected,
      onSppSelectedCustom,
      removeCustomParameter,
      activeParameters,
      isOctView,
      handleToggleFavorite,
      favoriteParams,
      selectedTargetType,
      isSettingsMode,
      compareMode,
      currentPlanLabel,
      comparedPlanLabel,
      isDisabled,
    ]
  );

  const renderParameterSections = useMemo(() => {
    return filteredParameterGroups.map(section => (
      <WidgetBox
        key={section.sectionTitle}
        title={section.sectionTitle}
        isExpandable={true}
        noBgColor={true}
        noHorizontalPadding={true}
        noVerticalPadding={true}
        isSmall={true}
        isExpanded={expandedGroup === section.sectionTitle}
        onToggleExpand={() =>
          setExpandedGroup(
            expandedGroup === section.sectionTitle ? null : section.sectionTitle
          )
        }
      >
        {renderParameterGroups(
          section.groups,
          false,
          section.groups.length === 1
        )}
      </WidgetBox>
    ));
  }, [filteredParameterGroups, renderParameterGroups, expandedGroup]);

  useEffect(() => {
    if (
      initialKeyParameters &&
      initialKeyParameters.length > 0 &&
      !hasInitializedKeyParams
    ) {
      const normalizedKeys = initialKeyParameters
        .map(key => Number(key))
        .filter(Number.isFinite);
      setFavoriteParams(new Set(normalizedKeys));
      setHasInitializedKeyParams(true);
    } else if (
      initialKeyParameters !== undefined &&
      initialKeyParameters.length === 0 &&
      !hasInitializedKeyParams
    ) {
      setHasInitializedKeyParams(true);
    } else if (initialKeyParameters === undefined && !hasInitializedKeyParams) {
    } else {
    }
  }, [initialKeyParameters, hasInitializedKeyParams, digitalTwinId]);

  useEffect(() => {
    const currentLength = Object.keys(customMeasurements).length;
    const measurementsAdded = currentLength > previousCustomMeasurementsLength;

    if (
      measurementsAdded &&
      currentLength > 0 &&
      expandedGroup !== "Custom Parameters"
    ) {
      setExpandedGroup("Custom Parameters");

      setTimeout(() => {
        const customParametersWidget = document.querySelector(
          ".custom-parameters-widget .App-widget-box-contain"
        );
        if (customParametersWidget) {
          customParametersWidget.scrollTop =
            customParametersWidget.scrollHeight;
        }
      }, 2000);
    }
    setPreviousCustomMeasurementsLength(currentLength);
  }, [customMeasurements, expandedGroup, previousCustomMeasurementsLength]);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const renderHeader = !isSettingsMode;

  return (
    <div className="spp-widget-container">
      {renderHeader && (
        <div className="spp-widget-general-info-and-controls">
          <div className="spp-widget-info">
            <OctTab
              isAlign={isOctView}
              targetType={selectedTargetType}
              compareMode={compareMode}
              currentPlanLabel={currentPlanLabel}
              comparedPlanLabel={comparedPlanLabel}
            />
          </div>
        </div>
      )}
      <div
        className="Spp-widget-wrapper"
        ref={widgetRef}
        style={isDisabled ? { pointerEvents: "none", opacity: 0.6 } : undefined}
      >
        {/* Key Parameters section */}
        {keyParameterGroups.length > 0 && (
          <WidgetBox
            title="Key Parameters"
            isExpandable={false}
            noBgColor={true}
            noHorizontalPadding={true}
            noVerticalPadding={true}
            isSmall={true}
            isExpanded={true}
          >
            {renderParameterGroups(keyParameterGroups, false, true)}
          </WidgetBox>
        )}

        {/* Render all parameter sections */}
        {renderParameterSections}

        {/* Custom Parameters section */}
        {customParameterGroups.length > 0 && (
          <WidgetBox
            title="Custom Parameters"
            isExpandable={true}
            noBgColor={true}
            noHorizontalPadding={true}
            noVerticalPadding={true}
            isSmall={true}
            isExpanded={expandedGroup === "Custom Parameters"}
            name="custom-parameters-widget"
            onToggleExpand={() =>
              setExpandedGroup(
                expandedGroup === "Custom Parameters"
                  ? null
                  : "Custom Parameters"
              )
            }
          >
            {renderParameterGroups(customParameterGroups, true)}
          </WidgetBox>
        )}
      </div>
    </div>
  );
};

export default SppWidget;
